import React from 'react';
import { Radio } from 'antd';

import { InputDate } from '../Input';
import ItemLookupSelect from '../common/ItemLookupSelect';
import UserPickerSelect from '../users/UserPickerSelect';
import TagSelect from '../tags/TagSelect';
import { DATE_RANGE, dateChoicesFor } from '../../utils/headerFilterDates';

/**
 * The value controls of a column header's filter for dates, people, tags and
 * items (KPR-1712): the pickers the filter builder's rows use, set up as its
 * list rows are, so what a person picks here is what they would pick there.
 * Each takes the panel's draft and hands back the next one.
 */

/** A date column: one choice of five (four on Created and Updated), and the two days of a custom range. */
export function DateChoice({ column, label, draft, onChange }) {
	const choices = dateChoicesFor(column);
	return (
		<>
			<Radio.Group
				className="header-filter__options"
				value={draft.preset}
				aria-label={label}
				onChange={event => onChange({ ...draft, preset: event.target.value })}
				options={choices}
			/>
			{draft.preset === DATE_RANGE ? (
				<div className="header-filter__days">
					{[['from', 'From'], ['to', 'To']].map(([end, words]) => (
						<div className="header-filter__end" key={end}>
							<span className="header-filter__end-label" aria-hidden="true">{words}</span>
							<InputDate
								elementName={`header-filter-${column.key}-${end}`}
								elementLabel={`${label}: ${words.toLowerCase()}`}
								value={draft[end] || null}
								onChange={(_name, stored) => onChange({ ...draft, [end]: stored || null })}
							/>
						</div>
					))}
				</div>
			) : null}
		</>
	);
}

/** People, tags or items, several allowed. */
export function PickedValues({ column, label, draft, onChange }) {
	const field = column.field;
	const values = Array.isArray(draft.values) ? draft.values : [];
	const change = next => onChange({ values: Array.isArray(next) ? next : (next ? [next] : []) });
	let picker = null;
	if (column.kind === 'user') {
		picker = (
			<UserPickerSelect
				className="header-filter__picker"
				collectionId={column.collectionId}
				multiple
				// A stored list may hold typed text where an id belongs: shown and
				// flagged, never sent to the by-id lookup (as in the builder).
				onlyAccountIds
				meMode="value"
				value={values}
				aria-label={label}
				placeholder="Pick people"
				onChange={change}
			/>
		);
	} else if (column.kind === 'tag') {
		const options = field.options || {};
		picker = (
			<TagSelect
				className="header-filter__picker"
				multiple
				value={values}
				collectionTags={options.tags}
				privateTags={options.mode === 'live' ? options.privateTags : null}
				itemTags
				itemTitles={options.itemTitles}
				aria-label={label}
				placeholder="Choose tags"
				onChange={change}
			/>
		);
	} else if (column.kind === 'lookup') {
		picker = (
			<ItemLookupSelect
				className="header-filter__picker"
				collectionId={column.collectionId}
				cardId={field.options?.lookupCardId}
				multiple
				// Picked records only: a title match is the builder's to write.
				titles={false}
				value={values}
				ariaLabel={label}
				placeholder="Pick items"
				onChange={change}
			/>
		);
	}
	return (
		<>
			{picker}
			<p className="header-filter__hint">{values.length > 1 ? 'Shows items with any of these.' : 'Pick one or more.'}</p>
		</>
	);
}
